/* ==========================================================================
   The single definition of what every fleet state looks like.
   --------------------------------------------------------------------------
   One colour per state, used identically everywhere: as a 6px dot in a badge,
   as a 2px edge bar on a table row, as a cell in the fleet matrix, and as the
   colour of a word. If a state's appearance needs to change, it changes here
   and nowhere else.

   Colour is never the sole carrier: every state also has a LABEL, and the two
   "no signal" states are drawn hollow so they are distinguishable from the
   filled ones without hue.
   ========================================================================== */

import type { Liveness, RobotStatus } from '../hooks/useRobots.ts';

/** Every tone the console can paint. Maps 1:1 onto a --st-* / semantic token. */
export type Tone =
  | 'active'
  | 'idle'
  | 'charging'
  | 'error'
  | 'maintenance'
  | 'live'
  | 'backfilling'
  | 'stale'
  | 'offline'
  | 'provisioning'
  | 'ok'
  | 'good'
  | 'warn'
  | 'accent'
  /** A measured quantity inside its limits. Steel — deliberately not green. */
  | 'nominal'
  | 'neutral';

export interface StateDescriptor {
  /** Human label. Sentence case — never uppercased in the markup. */
  label: string;
  tone: Tone;
  /** Hollow = nothing is arriving. Distinguishes "no data" from "data". */
  hollow: boolean;
  /** What the state actually means, for a title attribute. */
  hint: string;
}

/* Operational mode — what the robot is doing. Never connectivity. */
export const STATUS_STATE: Record<RobotStatus, StateDescriptor> = {
  active: {
    label: 'Active',
    tone: 'active',
    hollow: false,
    hint: 'Executing — mission, mapping or capture is running',
  },
  idle: {
    label: 'Idle',
    tone: 'idle',
    hollow: false,
    hint: 'Nominal and available — powered, not tasked',
  },
  charging: {
    label: 'Charging',
    tone: 'charging',
    hollow: false,
    hint: 'On the dock, replenishing',
  },
  error: {
    label: 'Error',
    tone: 'error',
    hollow: false,
    hint: 'Faulted — will not accept motion commands until cleared',
  },
  maintenance: {
    label: 'Maintenance',
    tone: 'maintenance',
    hollow: false,
    hint: 'Held out of service by an operator',
  },
};

/* Connectivity, derived server-side from telemetry recency. */
export const LIVENESS_STATE: Record<Liveness, StateDescriptor> = {
  live: {
    label: 'Live',
    tone: 'live',
    hollow: false,
    hint: 'Reporting in real time',
  },
  backfilling: {
    label: 'Backfilling',
    tone: 'backfilling',
    hollow: false,
    hint: 'Recent data, but backdated (store-and-forward) — not live',
  },
  /* `warn`, not the `stale` token, and this is the one liveness tone that had
     to move rather than be overridden at a call site.

     It was the only state whose colour depended on which surface you were
     standing on. Everything that projects through `matrixTone` — the register,
     the catalog, the census matrix, the map — drew it amber; everything that
     takes the literal palette drew it grey: telemetry's robot rows, the
     commands queue's Link column and record panel, and the LivenessBadge on
     the robot page, on teleop and in the map's robot card. One word, two
     colours, and no rule an operator could learn from the screen.

     Amber is the one that survives scrutiny. `warn` is what MATRIX_KEY names
     "not current", and "no recent data" is exactly that. Grey is the no-data
     family — offline and provisioning, both hollow — which is a DIFFERENT
     fact: a stale robot is still there and still reporting, just not lately.
     And `backfilling` is amber in both palettes, so grey here inverted the
     severity: the milder state shouted and the worse one whispered.

     The obvious alternative was to leave the tone alone and re-tint
     `--st-stale` amber, which would have kept connectivity's own palette
     indirection. It is wrong: `CMD_TONE.pending` is also `stale`, and a pending
     command — "accepted by the cloud, not yet handed to the robot" — is an
     ordinary waiting state that must not go amber. The token keeps its grey for
     that; this state stops borrowing it. */
  stale: {
    label: 'Stale',
    tone: 'warn',
    hollow: false,
    hint: 'No recent data',
  },
  offline: {
    label: 'Offline',
    tone: 'offline',
    hollow: true,
    hint: 'Was reporting before, now unreachable',
  },
  provisioning: {
    label: 'Provisioning',
    tone: 'provisioning',
    hollow: true,
    hint: 'Registered, but has never phoned home',
  },
};

/**
 * Threshold reading for a bounded value.
 *
 * By default LOW is bad — battery, disk free, signal — so `warn`/`bad` are
 * lower bounds (`bad` when value <= bad). Pass `invert` for quantities where
 * HIGH is bad — CPU load, temperature, disk used — and the same two numbers
 * become upper bounds. Thresholds are always written in the units of the
 * value itself, so a call site reads as the operator would say it aloud:
 * `level(cpu, 75, 90, true)` = "warn at 75%, bad at 90%".
 */
export function level(
  value: number | null | undefined,
  warn: number,
  bad: number,
  invert = false,
): 'ok' | 'warn' | 'bad' | 'unknown' {
  if (value == null || Number.isNaN(value)) return 'unknown';
  if (invert) {
    if (value >= bad) return 'bad';
    if (value >= warn) return 'warn';
    return 'ok';
  }
  if (value <= bad) return 'bad';
  if (value <= warn) return 'warn';
  return 'ok';
}

/**
 * Threshold reading → tone, so a meter and a badge agree on colour.
 *
 * `ok` resolves to `nominal` (steel), NOT to green. A fleet of 28 healthy
 * batteries painted green is 28 pixels of colour that carry no information,
 * and it buries the two cells that do. Saturation is spent only once a
 * threshold has actually been crossed — so the eye goes straight to the
 * amber and the red. Green stays reserved for "complete / armed / live".
 */
export function levelTone(l: 'ok' | 'warn' | 'bad' | 'unknown'): Tone {
  switch (l) {
    case 'ok':
      return 'nominal';
    case 'warn':
      return 'warn';
    case 'bad':
      return 'error';
    default:
      return 'neutral';
  }
}

/**
 * The tone a bounded reading is entitled to, given what its caller asked for.
 *
 * This is the one place the console decides whether a component may be told
 * what colour to be. Inside its limits a caller may name the tone — signal
 * strength and fill level are facts, not judgements, and steel is usually the
 * right answer. The moment a threshold is crossed the reading's own level
 * wins, unconditionally. That asymmetry is the point: a call site can say
 * "this is unremarkable" but no call site can say "this 8% battery is fine",
 * which is exactly the mistake a literal `tone="good"` used to allow.
 *
 * Meter, Gauge and Sparkline all resolve through this, so the trace, the bar
 * and the arc reading the same quantity can never disagree about its colour.
 */
export function boundedTone(
  lv: 'ok' | 'warn' | 'bad' | 'unknown',
  tone: Tone | undefined,
): Tone {
  if (lv === 'warn' || lv === 'bad') return levelTone(lv);
  return tone ?? levelTone(lv);
}

/**
 * The same state, projected onto the health scale for a FLEET-SCALE surface.
 *
 * A badge names one robot, so `Active` may be green and `Live` may be green —
 * the reader is looking at a single fact. A matrix is 168 facts at once, and
 * there `levelTone`'s rule has to apply to categorical states too: paint steel
 * unless a human needs to do something. Twenty-eight green "Live" cells and a
 * five-hue mode row make a rainbow with no break in it, and the two faults —
 * the entire reason the screen exists — stop being findable.
 *
 * What survives as colour:
 *   error        → red      a fault
 *   maintenance  → purple   deliberately held out of service by an operator
 *   stale/backfill/warn → amber   the reading is not current
 *   offline, provisioning → grey, and the caller keeps `hollow` so
 *                          "no data" is told apart by shape, not only hue
 * Everything else — active, idle, charging, live, ok, good — is `nominal`.
 *
 * The word itself is never projected: the hover readout and the screen reader
 * still say "Charging", so no information is lost, only saturation.
 */
export function matrixTone(t: Tone): Tone {
  switch (t) {
    case 'error':
    case 'maintenance':
    case 'offline':
      return t;
    /* "Registered but never phoned home" and "was reporting, now unreachable"
       are the same fact to a matrix: nothing is arriving. Both draw hollow
       grey, which is the colour MATRIX_KEY names "no data" — a hue on screen
       that the legend does not name is a legend that lies. The two are still
       told apart by the readout and by LivenessBadge, where the reader is
       looking at one robot rather than 168 readings. */
    case 'provisioning':
      return 'offline';
    case 'warn':
    case 'backfilling':
    case 'stale':
      return 'warn';
    default:
      return 'nominal';
  }
}

/** The tone key that describes every colour `matrixTone` can produce. */
export const MATRIX_KEY: readonly { tone: Tone; label: string; hollow?: boolean }[] = [
  { tone: 'nominal', label: 'nominal' },
  { tone: 'warn', label: 'not current' },
  { tone: 'error', label: 'fault' },
  { tone: 'maintenance', label: 'held' },
  { tone: 'offline', label: 'no data', hollow: true },
];
